import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { BlogCard } from "./blog-card";
import { CategoryFilter } from "./category-filter";
import { Pagination } from "./pagination";
import { Loader2 } from "lucide-react";
import { api } from "./api";
import "../../../css/blogs.css"; // Import the CSS file

const Blogs = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "";
  const pageParam = searchParams.get("page")
    ? parseInt(searchParams.get("page"))
    : 1;

  const [categorySlug, setCategorySlug] = useState(categoryParam);
  const [page, setPage] = useState(pageParam);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [posts, setPosts] = useState([]);
  const [meta, setMeta] = useState({
    page: 1,
    total: 0,
    limit: 9,
  });
  const [loading, setLoading] = useState({ posts: true });

  // Reset page when category changes
  useEffect(() => {
    // Only reset page if the category actually changed from the URL param
    if (categorySlug !== categoryParam) {
      setPage(1);
    }
  }, [categorySlug, categoryParam]);

  // Update URL when category or page changes
  useEffect(() => {
    const params = new URLSearchParams();

    if (categorySlug !== "") {
      // Use 'category' for the URL param to match initial load
      params.set("category", categorySlug);
    }

    if (page > 1) {
      params.set("page", page.toString());
    }

    // Use replace: true to avoid adding multiple history entries when filtering/paging
    setSearchParams(params, { replace: true });
  }, [categorySlug, page, setSearchParams]);

  // Fetch posts and set featured post as first post of current page
  useEffect(() => {
    setLoading({ posts: true }); // Set loading true when fetching starts
    const fetchPosts = async () => {
      try {
        const response = await api.posts.getAll({
          categorySlug,
          page,
          limit: 9,
          status: "published",
        });

        // Ensure response.posts is an array
        const validPosts = Array.isArray(response?.posts) ? response.posts : [];

        // Set the first post as the featured post
        setFeaturedPost(validPosts[0] || null);
        setPosts(validPosts);
        setMeta({
          page: response?.page || 1,
          total: response?.total || 0,
          limit: response?.limit || 9,
        });
      } catch (error) {
        console.error("Failed to fetch posts:", error);
        setPosts([]); // Clear posts on error
        setFeaturedPost(null);
      } finally {
        setLoading({ posts: false });
      }
    };

    fetchPosts();
  }, [categorySlug, page]);

  const handleCategoryChange = (newCategory) => {
    setCategorySlug(newCategory);
    // Page reset is handled by the useEffect hook watching categorySlug
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    // Scroll to top when changing pages
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="blogs-container">
      <main className="blogs-main">
        {/* Removed div wrapper around sections */}
        {/* Featured Post Section */}
        <section className="blogs-section">
          {loading.posts ? (
            <div className="blogs-featured-loader">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            featuredPost && <BlogCard post={featuredPost} featured />
          )}
        </section>

        {/* Category Filter */}
        <section className="blogs-section">
          <CategoryFilter
            activeCategory={categorySlug}
            onCategoryChange={handleCategoryChange}
          />
        </section>

        {/* Blog Posts Grid */}
        <section>
          {loading.posts ? (
            <div className="blogs-grid-loader">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="blogs-grid-loader-item" />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="blogs-no-posts">
              <h3 className="blogs-no-posts-title">No posts found</h3>
              <p className="blogs-no-posts-message">
                No blog posts matching your criteria were found. Try changing
                the category.
              </p>
            </div>
          ) : (
            <div className="blogs-grid">
              {posts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </section>

        {/* Pagination */}
        {meta.total > meta.limit && !loading.posts && (
          <section className="blogs-section">
            <Pagination
              currentPage={meta.page}
              totalPages={Math.ceil(meta.total / meta.limit)}
              onPageChange={handlePageChange}
            />
          </section>
        )}
        {/* Removed closing div */}
      </main>
    </div>
  );
};

export default Blogs;
